import React, { useState } from 'react';

import type { MemberDTO } from '../../services/apiClient';
import { ExternalLink } from 'lucide-react';

import FormField from '../../components/common/FormField';
import { MemberTypeConstants } from '../../constants/MemberTypeConstants';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useMemberTypes } from '../../hooks/useMemberTypeApi';
import handleChange from '../../utils/formUtils/handleChange';
import Form from '../common/Form';
import FormSection from '../common/FormSection';

interface MemberFormProps {
  initialValues: MemberDTO;
  onSubmit: (data: MemberDTO) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const FORM_SECTIONS = {
  profilePicture: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  billing: {
    id: 'billing',
    title: 'Billing',
  },
} as const;

const MemberForm: React.FC<MemberFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<MemberDTO>(initialValues);
  const { data: memberTypes } = useMemberTypes();

  const sections = Object.values(FORM_SECTIONS);

  const memberTypeOptions = (memberTypes || [])
    .filter((t) => t.id)
    .map((t) => ({ value: t.id as string, label: t.name || '' }));

  const validationRules: ValidationRules = {
    // Member Section
    firstName: [{ required: true, message: 'First Name is required' }],
    lastName: [{ required: true, message: 'Last Name is required' }],
    email: [{ required: true, message: 'Email is required' }],
  };

  const { errors, validate } = useFormValidation<MemberDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  // Lifetime members get full access without ever paying (SecuredMiddleware's gate checks only
  // Active), so picking Lifetime here should flip Active on immediately rather than making the
  // admin remember a second step - but never flips it back off if they switch away again, that
  // stays a manual decision like every other Active change on this form.
  const handleMemberTypeChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ): void => {
    const memberTypeId = e.target.value || undefined;
    setFormData((prev) => ({
      ...prev,
      memberTypeId,
      active:
        memberTypeId === MemberTypeConstants.LIFETIME_ID ? true : prev.active,
    }));
  };

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
    >
      <FormSection
        id={FORM_SECTIONS.profilePicture.id}
        title={FORM_SECTIONS.profilePicture.title}
        columns={2}
      >
        <FormField
          id="membershipNumber"
          label="Membership Number"
          type="text"
          value={formData.membershipNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Membership Number"
        />
        <FormField
          id="firstName"
          label="First Name"
          type="text"
          value={formData.firstName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter First Name"
          required
          error={errors.firstName}
        />
        <FormField
          id="lastName"
          label="Last Name"
          type="text"
          value={formData.lastName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Last Name"
          required
          error={errors.lastName}
        />
        <FormField
          id="email"
          label="Email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Email"
          required
          error={errors.email}
        />
        <FormField
          id="phone"
          label="Phone"
          type="text"
          value={formData.phone || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Email"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.billing.id}
        title={FORM_SECTIONS.billing.title}
      >
        <FormField
          id="memberTypeId"
          label="Membership Type"
          type="dropdown"
          value={formData.memberTypeId || ''}
          onChange={handleMemberTypeChange}
          options={memberTypeOptions}
          placeholder="Select a Membership Type"
          subtext="Lifetime members get full access without ever needing to pay."
        />
        <FormField
          id="active"
          label="Active"
          type="checkbox"
          value={formData.active ?? false}
          onChange={(e) => handleChange(e, setFormData)}
          subtext="Set automatically on payment/cancellation via Stripe - toggle manually here to block or restore a member outside of that."
        />
        {formData.stripeCustomerId ? (
          <div className="flex items-center gap-2 text-sm text-ink dark:text-ink-muted">
            <span className="font-mono">{formData.stripeCustomerId}</span>
            <a
              href={`https://dashboard.stripe.com/customers/${formData.stripeCustomerId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-primary-deep hover:underline"
            >
              View in Stripe <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        ) : (
          <p className="text-sm text-ink dark:text-ink-muted">
            No Stripe customer yet - this is set the first time the member
            completes a payment.
          </p>
        )}
      </FormSection>
    </Form>
  );
};

export default MemberForm;
